import { PortableText, type PortableTextReactComponents } from "@portabletext/react"
import type { PortableTextTextBlock } from "@sanity/types"
import { Children, type ReactElement, type ReactNode } from "react"
import { slugify } from "utils/slugify"

const sanitizeHeadingChildrenToText = (children: NonNullable<ReactNode>): string => {
  /*
   * Mostly text is just text, but in some cases the structure of the heading can be like (due to copy paste perhaps)
   * <h2>foo <strong>bar</strong></h2>
   * in which case we need to define the text as "foo bar", and hence map the text values
   */

  if (typeof children === "string") {
    return children
  }

  if (Array.isArray(children)) {
    return children
      .map((node: string | ReactElement) => {
        if (typeof node === "string") {
          return node
        }
        return node?.props?.text
      })
      .filter((t): t is string => !!t)
      .join("")
  }

  if (process.env.NODE_ENV === "development") {
    console.warn(
      `Please double check heading content output. Given heading was "${children}" of type (${typeof children})`,
    )
  }
  // true | number | a_serious_mistake
  return `${children}`
}

export const basicComponents: Omit<Partial<PortableTextReactComponents>, "types"> = {
  marks: {
    link: ({ children, value }) => {
      if (!value.href) {
        // Link without href crashes the whole document. This scenario should be made impossible from Sanity
        return <span className="text-red-500">{children}(missing link)</span>
      }
      return (
        <a href={value.href} className="text-fg-emerald hover:underline">
          {children}
        </a>
      )
    },
    strong: ({ children }) => <strong className="font-medium text-fg-default">{children}</strong>,
  },
  block: {
    h1: ({ children }) => {
      if (!children) {
        return null
      }
      const headingText = sanitizeHeadingChildrenToText(children)

      return (
        <h1
          className="font-medium text-2xl md:text-3xl mb-4 md:mb-6 pt-2 md:pt-4 [&_strong]:font-medium tracking-tight [.changelog_&]:text-lg [.changelog_&]:md:text-xl"
          data-anchor={slugify(headingText)}
        >
          {headingText}
        </h1>
      )
    }, // Not needed, but make sure article never renders <h1> even if CMS changes
    h2: ({ children }) => {
      if (!children) {
        return null
      }
      const headingText = sanitizeHeadingChildrenToText(children)

      return (
        <h2
          className="font-medium text-2xl md:text-3xl mb-4 md:mb-6 pt-2 md:pt-4 [&_strong]:font-medium tracking-tight [.changelog_&]:text-base [.changelog_&]:md:text-lg"
          data-anchor={slugify(headingText)}
        >
          {headingText}
        </h2>
      )
    },
    h3: ({ children }) => {
      if (!children) {
        return null
      }
      const headingText = sanitizeHeadingChildrenToText(children)
      return (
        <h3
          className="font-medium text-xl md:text-2xl mb-4 md:mb-6 pt-2 md:pt-4 [&_strong]:font-medium tracking-tight [.changelog_&]:text-sm [.changelog_&]:md:text-base"
          data-anchor={slugify(headingText)}
        >
          {headingText}
        </h3>
      )
    },
    blockquote: ({ children }) => (
      <blockquote className="mb-4 md:mb-6 pt-1 md:pt-2 border-l-4 border-neutral-600 pl-4 italic">
        {children}
      </blockquote>
    ),
    normal: ({ children }) =>
      !children || Children.toArray(children).filter(Boolean).length === 0 ? (
        <p className="mb-2 md:mb-4 text-fg-secondary" /> // Render normal line space when someone outputs empty line
      ) : (
        <p className="mb-4 md:mb-6 text-base text-fg-secondary">{children}</p>
      ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="text-fg-secondary mb-4 md:mb-6 list-disc flex flex-col gap-3 [li>&]:mt-3 [li>&]:mb-0 only:mb-0">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="text-fg-secondary mb-4 md:mb-6 list-decimal flex flex-col gap-3">
        {children}
      </ol>
    ),
  },
  listItem: {
    // Ex. 1: customizing common list types
    bullet: ({ children }) => <li className="mb-1 ml-4 pl-1">{children}</li>,
    number: ({ children }) => <li className="mb-1 ml-4 pl-1">{children}</li>,
  },
}

export const SanityBasicContentRenderer = ({ content }: { content: PortableTextTextBlock[] }) => {
  if (!content) {
    // Failsafe
    return null
  }
  return <PortableText value={content} components={basicComponents} />
}
